import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Badge, Box, Container, Drawer, IconButton, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallOutlined from '@mui/icons-material/CallOutlined';
import MailOutline from '@mui/icons-material/MailOutlineSharp';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccordionMenu from './AccordionMenu';
import BazarCard from './BazarCard';
import MiniCart from './MiniCart';
import SearchBox from './SearchBox';
import { GlobalContext } from '../context/GlobalState';
import { subCategoryDisplayName } from '../utils/categoryLabels';
import {
  assets,
  categoryLandingRoutesById,
  layoutConstant,
  routerUrls,
  stringConstants
} from 'constants';

/**
 * The Aurum header: a centred logo flanked by the category nav and the tools.
 *
 * Currently mounted only on /home-preview (see Layout.jsx) while the design is
 * reviewed; the rest of the site still runs Topbar + Header + NavBar.
 */

const ContactStrip = styled('div')(({ theme }) => ({
  height: layoutConstant.topbarHeight,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontSize: 12,
  letterSpacing: '0.08em'
}));

const HeadRow = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 30,
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`
}));

// The underline grows out of the left edge on hover, rather than fading in.
// Category links read gold, bold and underlined at rest. The type takes gold.text -
// the only gold with enough contrast to read as type - while the rule under it takes
// gold.fill, which is a surface/border colour and must never be set on text. The rule
// no longer wipes in on hover: it is always at full width, and deepens instead.
const navLinkSx = {
  position: 'relative',
  display: 'inline-block',
  paddingY: '4px',
  fontSize: '15px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'gold.text',
  cursor: 'pointer',
  transition: 'color 150ms ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '-2px',
    height: '1px',
    backgroundColor: 'gold.fill',
    transition: 'background-color 150ms ease-in-out'
  },
  '&:hover': { color: 'gold.deep' },
  '&:hover::after': { backgroundColor: 'gold.deep' }
};

const iconBtnSx = {
  width: 40,
  height: 40,
  color: 'text.primary',
  transition: 'background-color 150ms ease-in-out, color 150ms ease-in-out',
  '&:hover': { backgroundColor: 'background.tile', color: 'gold.text' }
};

// Round the shared SearchBox field into a soft pill on a paper fill - a modern,
// always-present search bar. Applied to both the desktop and mobile placements.
const searchFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 999,
    backgroundColor: 'background.paper'
  }
};

export default function AurumHeader() {
  const { cartList, menu, setMenu } = useContext(GlobalContext);
  const [cartOpen, setCartOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (menu) return undefined;
    let cancelled = false;
    axios.get('/menu').then(({ data }) => {
      if (!cancelled) setMenu(data);
    });
    return () => {
      cancelled = true;
    };
  }, [menu, setMenu]);

  const nav = menu?.map((category) => (
    <Box
      key={category.id}
      sx={{ position: 'relative', '&:hover > .child-nav-item': { display: 'block' } }}>
      <Box
        component={Link}
        to={categoryLandingRoutesById[category.id] || routerUrls.root}
        sx={{ ...navLinkSx, textDecoration: 'none' }}>
        {category.name_bg}
      </Box>
      {/* Padding, not margin: the gap under the link has to stay hoverable. */}
      <Box
        className="child-nav-item"
        sx={{
          display: 'none',
          position: 'absolute',
          left: 0,
          top: '100%',
          pt: '18px',
          zIndex: 1000
        }}>
        <BazarCard
          elevation={0}
          sx={{
            py: '0.5rem',
            minWidth: '230px',
            backgroundColor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none'
          }}>
          {categoryLandingRoutesById[category.id] && (
            <Link
              to={categoryLandingRoutesById[category.id]}
              style={{ color: 'inherit', textDecoration: 'none' }}>
              <MenuItem sx={{ fontWeight: 600 }}>Всички категории</MenuItem>
            </Link>
          )}
          {category.subCategories.map((subCategory) => (
            <Link
              key={subCategory.id}
              to={`${routerUrls.products}/${category.slug_bg}/${subCategory.slug_bg}/${category.id}/${subCategory.id}`}
              style={{ color: 'inherit', textDecoration: 'none' }}>
              <MenuItem>{subCategoryDisplayName(subCategory.name_bg)}</MenuItem>
            </Link>
          ))}
        </BazarCard>
      </Box>
    </Box>
  ));

  return (
    <Box component="header">
      <ContactStrip>
        <Container
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CallOutlined fontSize="small" sx={{ p: 0.5, color: 'gold.fill' }} />
              <span>{stringConstants.phone}</span>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
              <MailOutline fontSize="small" sx={{ p: 0.5, color: 'gold.fill' }} />
              <span>{stringConstants.email}</span>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <AccessTimeIcon fontSize="small" sx={{ p: 0.5, color: 'gold.fill' }} />
            <span>{stringConstants.workingHours}</span>
          </Box>
        </Container>
      </ContactStrip>

      <HeadRow>
        {/* Main row: logo · full search bar · cart. The search is always visible
            so you can search from anywhere. */}
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 3 },
            height: { xs: layoutConstant.mobileHeaderHeight, md: 84 }
          }}>
          <IconButton
            aria-label="Меню"
            onClick={() => setDrawerOpen(true)}
            sx={{ ...iconBtnSx, display: { xs: 'inline-flex', md: 'none' } }}>
            <MenuIcon />
          </IconButton>

          <Box
            component={Link}
            to={routerUrls.root}
            sx={{ display: 'block', lineHeight: 0, flex: 'none' }}>
            <Box
              component="img"
              src={assets.logo}
              alt="GoToBed"
              sx={{ height: { xs: 26, md: 34 }, width: 'auto', display: 'block' }}
            />
          </Box>

          {/* Desktop: full search bar fills the centre of the row */}
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              ...searchFieldSx
            }}>
            <SearchBox />
          </Box>

          <Badge
            badgeContent={cartList.length}
            sx={{
              ml: { xs: 'auto', md: 0 },
              '& .MuiBadge-badge': { backgroundColor: 'gold.fill', color: 'text.primary' }
            }}>
            <IconButton aria-label="Количка" onClick={() => setCartOpen(true)} sx={iconBtnSx}>
              <ShoppingCartIcon />
            </IconButton>
          </Badge>
        </Container>

        {/* Mobile: full search bar on its own row, always visible */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.25,
            ...searchFieldSx
          }}>
          <SearchBox />
        </Box>

        {/* Category nav on its own centred row (desktop) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
          <Container
            component="nav"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '26px',
              alignItems: 'center',
              height: 48
            }}>
            {nav}
          </Container>
        </Box>
      </HeadRow>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { backgroundColor: 'background.default' } }}>
        <Box sx={{ width: 280, p: 2 }} role="presentation">
          <SearchBox />
          {menu && (
            <Box sx={{ mt: 2 }} onClick={() => setDrawerOpen(false)}>
              <AccordionMenu categoryList={menu} />
            </Box>
          )}
        </Box>
      </Drawer>

      <Drawer anchor="right" open={cartOpen} onClose={() => setCartOpen(false)}>
        <MiniCart toggleSidenav={() => setCartOpen(false)} />
      </Drawer>
    </Box>
  );
}
